import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './HelpModal.css';

const HelpModal = ({ documentTitle, documentUrl, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Handle Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleIframeLoad = () => {
    setLoading(false);
    setError('');
  };

  const handleIframeError = () => {
    setLoading(false);
    setError('Failed to load document. Please try downloading it directly.');
  };

  const handleDownload = () => {
    window.open(documentUrl, '_blank');
  };

  return (
    <div className="modal-overlay help-modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content help-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{documentTitle}</h2>
          <button
            className="modal-close"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modal-body help-modal-body">
          {loading && (
            <div className="help-modal-loading">
              <div className="spinner-large"></div>
              <p>Loading document...</p>
            </div>
          )}

          {error && (
            <div className="help-modal-error">
              <svg width="48" height="48" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p>{error}</p>
              <button className="help-modal-download-btn" onClick={handleDownload}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Download Document
              </button>
            </div>
          )}

          <iframe
            src={documentUrl}
            className="help-modal-iframe"
            title={documentTitle}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            style={{ display: loading || error ? 'none' : 'block' }}
          />
        </div>

        <div className="modal-footer help-modal-footer">
          <button
            className="modal-button modal-button--secondary"
            onClick={handleDownload}
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            Open in New Tab
          </button>
          <button
            className="modal-button modal-button--primary"
            onClick={onClose}
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

HelpModal.propTypes = {
  documentTitle: PropTypes.string.isRequired,
  documentUrl: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default HelpModal;
